import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ArrowRight, ChevronDown, Sparkles } from 'lucide-react';
import Reveal from '@/components/saas/Reveal';
import MarketingCtaBand from '@/components/saas/MarketingCtaBand';
import { parseMarketingSections } from '@/lib/parse-marketing-html';
import { formatTierPrice, tierFeatures } from '@/lib/pricing-tier';
import { getCtaBanner, findSection, type HomepageSection, type PricingTier } from './helpers';
import ProseFallback from './ProseFallback';

interface Props {
  page: { title?: string; content?: string };
  sections?: HomepageSection[];
  siteName: string;
}

interface FaqItem {
  question: string;
  answer: string;
}

/**
 * Where a tier's button goes.
 *
 * A tier that names a `plan_slug` carries it into signup, which hands it to
 * `resolvePostAuthDest` → `/account/billing?plan=…` → Stripe Checkout. Without
 * one the button is marketing only: an explicit `cta_link` if the editor set
 * one, else plain signup. (Every tier used to fall in that last bucket, which
 * is why a tenant's pricing page could not sell anything.)
 */
function ctaHref(tier: PricingTier): string {
  if (tier.plan_slug) return `/signup?plan=${encodeURIComponent(tier.plan_slug)}`;
  return tier.cta_link || '/signup';
}

export default function TenantPricingPage({ page, sections, siteName }: Props) {
  const { t } = useTranslation('saas');
  const parsed = useMemo(() => parseMarketingSections(page.content), [page.content]);
  const cta = getCtaBanner(sections);

  const tiersSection = findSection(sections, 'pricing_tiers');
  const tiers: PricingTier[] = Array.isArray(tiersSection?.content?.tiers) ? tiersSection!.content.tiers : [];

  const faqSection = findSection(sections, 'faq');
  const faq: FaqItem[] = Array.isArray(faqSection?.content?.items) ? faqSection!.content.items : [];

  const heading = page.title || t('tenantMarketing.viewPricing');
  const subheading =
    (tiersSection?.subtitle && String(tiersSection.subtitle)) || parsed.intro[0] || '';

  return (
    <>
      <section className="bg-cream">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:px-8">
          <h1 className="font-display text-4xl font-bold text-sage-900 sm:text-5xl">{heading}</h1>
          {subheading && <p className="mt-4 text-lg text-sage-600">{subheading}</p>}
        </div>
      </section>

      {tiers.length > 0 ? (
        <section className="bg-surface">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {tiers.map((tier, idx) => {
                // price is a number (0 = free) from the generator, or a label
                // like "$39"/"Custom"/"Contact us" from the admin builder. Both
                // renderers share one formatter so a hand-written "$39" keeps
                // its /interval and a generated `39` still gets its "$".
                const { label: priceLabel, isAmount } = formatTierPrice(
                  tier.price,
                  t('tenantMarketing.free'),
                );
                return (
                  <div
                    key={tier.id || `${tier.name}-${idx}`}
                    className={`flex flex-col rounded-2xl border-2 bg-surface p-8 ${
                      tier.featured ? 'border-sage-700 shadow-xl ring-4 ring-sage-100' : 'border-sage-200'
                    }`}
                  >
                    {tier.featured && (
                      <div className="inline-flex w-fit rounded-full bg-cta px-3 py-1 text-xs font-semibold text-cta-fg">
                        {t('common.mostPopular')}
                      </div>
                    )}
                    <h2 className="mt-3 font-display text-2xl font-bold text-sage-900">{tier.name}</h2>
                    {tier.description && <p className="mt-1 text-sm text-sage-600">{tier.description}</p>}
                    <div className="mt-6 flex items-baseline gap-1">
                      <span
                        className={
                          isAmount
                            ? 'font-display text-5xl font-bold text-sage-900'
                            : 'font-display text-4xl font-bold text-sage-900'
                        }
                      >
                        {priceLabel}
                      </span>
                      {isAmount && tier.interval && (
                        <span className="text-sage-500">/{tier.interval}</span>
                      )}
                    </div>
                    {tier.note && (
                      <p className="mt-3 rounded-lg bg-sage-50 px-3 py-2 text-xs font-medium leading-relaxed text-sage-700">
                        {tier.note}
                      </p>
                    )}
                    <Link
                      to={ctaHref(tier)}
                      className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold transition-colors ${
                        tier.featured
                          ? 'bg-cta text-cta-fg hover:bg-cta-deep'
                          : 'border border-sage-300 text-sage-800 hover:bg-sage-50'
                      }`}
                    >
                      {tier.cta_text || t('tenantMarketing.getStarted')}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                    {tierFeatures(tier.features).length > 0 && (
                      <ul className="mt-8 space-y-3">
                        {tierFeatures(tier.features).map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-sage-700">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage-600" aria-hidden="true" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : (
        <ProseFallback html={page.content || ''} />
      )}

      {faq.length > 0 && (
        <section className="border-y border-sage-200 bg-sage-50">
          <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-8">
            <h2 className="text-center font-display text-3xl font-bold text-sage-900">
              {(faqSection?.title && String(faqSection.title)) || t('tenantMarketing.faqHeading')}
            </h2>
            <div className="mt-4 flex items-center justify-center gap-3" aria-hidden="true">
              <div className="h-px w-12 bg-sage-300" />
              <Sparkles className="h-4 w-4 text-sage-400" />
              <div className="h-px w-12 bg-sage-300" />
            </div>
            <div className="mt-10 divide-y divide-sage-200 border-y border-sage-200">
              {faq.map((item, i) => (
                <Reveal key={i}>
                  <details className="group">
                    <summary className="flex w-full cursor-pointer items-center justify-between gap-4 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 [&::-webkit-details-marker]:hidden">
                      <span className="font-display text-base font-semibold text-sage-900 transition-colors group-hover:text-sage-700">
                        {item.question}
                      </span>
                      <ChevronDown
                        className="h-5 w-5 shrink-0 text-sage-400 transition-transform duration-200 group-open:rotate-180"
                        aria-hidden="true"
                      />
                    </summary>
                    <div className="pb-5 pr-12">
                      <p className="text-sm leading-relaxed text-sage-600">{item.answer}</p>
                    </div>
                  </details>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <MarketingCtaBand
        heading={cta.heading || t('tenantMarketing.ctaHeadingFallback')}
        subheading={cta.subheading || t('tenantMarketing.ctaSubheadingFallback', { name: siteName })}
        primaryLabel={cta.ctaText || t('tenantMarketing.getStarted')}
        primaryTo={cta.ctaLink || '/signup'}
        secondaryLabel={t('tenantMarketing.viewFeatures')}
        secondaryTo="/features"
      />
    </>
  );
}
